import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { LogIn, Eye, EyeOff } from 'lucide-react'
import { loginUser } from '../components/Api.jsx'
import bybLogo from '../assets/byb-logo.png'

export default function LoginPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [showPassword, setShowPassword] = useState(false)

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const response = await loginUser(email, password)
      console.log("Full login response:", response)
      console.log("Response status:", response.status)
      console.log("Response data:", response.data)

      // Check if response is successful
      if (response.status === 200 && response.data) {
        let userDetails, tokens

        // Handle different possible response structures
        console.log("Checking response structure...")

        if (response.data.data && response.data.data.user_details) {
          // Structure: response.data.data.user_details
          console.log("Using structure: response.data.data.user_details")
          userDetails = response.data.data.user_details
          tokens = response.data.data
        } else if (response.data.data && response.data.data.userDetails) {
          // Structure: response.data.data.userDetails
          console.log("Using structure: response.data.data.userDetails")
          userDetails = response.data.data.userDetails
          tokens = response.data.data
        } else if (response.data.user_details) {
          // Structure: response.data.user_details (YOUR API STRUCTURE)
          console.log("Using structure: response.data.user_details")
          userDetails = response.data.user_details
          tokens = response.data
        } else if (response.data.userDetails) {
          // Structure: response.data.userDetails
          console.log("Using structure: response.data.userDetails")
          userDetails = response.data.userDetails
          tokens = response.data
        } else if (response.data.data) {
          // Structure: response.data.data (userDetails might be at root level)
          console.log("Using structure: response.data.data")
          userDetails = response.data.data
          tokens = response.data
        } else if (response.data.user) {
          // Structure: response.data.user (common in some APIs)
          console.log("Using structure: response.data.user")
          userDetails = response.data.user
          tokens = response.data
        } else if (response.data) {
          // Structure: response.data (everything at root level)
          console.log("Using structure: response.data (root level)")
          userDetails = response.data
          tokens = response.data
        } else {
          // Fallback: assume response contains everything
          console.log("Using fallback structure: response")
          userDetails = response
          tokens = response
        }

        console.log("Extracted userDetails:", userDetails)
        console.log("Extracted tokens:", tokens)

        // Extract user information with comprehensive fallbacks (prioritizing your API structure)
        const userId = userDetails?.user_id || userDetails?.userId || userDetails?.id ||
            userDetails?.ID || userDetails?.userID ||
            tokens?.userId || tokens?.id || tokens?.user_id

        const username = userDetails?.username || userDetails?.userName || userDetails?.name ||
            userDetails?.user_name || email

        const userEmail = userDetails?.email || userDetails?.emailAddress || email

        const firstName = userDetails?.first_name || userDetails?.firstName || userDetails?.name ||
            userDetails?.givenName || userDetails?.given_name || ''

        const lastName = userDetails?.last_name || userDetails?.lastName || userDetails?.surname ||
            userDetails?.familyName || userDetails?.family_name || ''

        const role = userDetails?.role || userDetails?.userRole || userDetails?.user_role ||
            userDetails?.authorities?.[0] || 'USER'

        // Extract tokens with comprehensive fallbacks (prioritizing your API structure)
        const accessToken = tokens?.access_token || tokens?.accessToken || tokens?.token ||
            tokens?.jwt || tokens?.bearer || tokens?.authToken ||
            userDetails?.accessToken || userDetails?.access_token || userDetails?.token

        const refreshToken = tokens?.refresh_token || tokens?.refreshToken || tokens?.refresh ||
            tokens?.refresh_token || ''

        const tokenType = tokens?.token_type || tokens?.tokenType || tokens?.type ||
            tokens?.bearer || 'Bearer'

        console.log("Extracted values:", {
          userId, username, userEmail, firstName, lastName, role,
          accessToken, refreshToken, tokenType
        })

        // Debug: Show all available keys in the response
        console.log("All keys in response.data:", Object.keys(response.data || {}))
        console.log("All keys in userDetails:", Object.keys(userDetails || {}))
        console.log("All keys in tokens:", Object.keys(tokens || {}))

        // Validate required fields with detailed error
        if (!userId) {
          console.error("Missing userId. Available fields:", Object.keys(userDetails || {}))
          throw new Error(`Invalid response structure: missing userId. Available fields: ${Object.keys(userDetails || {}).join(', ')}`)
        }

        if (!accessToken) {
          console.error("Missing accessToken. Available fields:", Object.keys(tokens || {}))
          throw new Error(`Invalid response structure: missing accessToken. Available fields: ${Object.keys(tokens || {}).join(', ')}`)
        }

        // Check if user has ADMIN or SUPER_ADMIN role
        if (role !== 'ADMIN' && role !== 'SUPER_ADMIN') {
          throw new Error('Access denied. Only administrators can access this panel.')
        }

        // Store in localStorage
        localStorage.setItem('userId', userId)
        localStorage.setItem('username', username)
        localStorage.setItem('email', userEmail)
        localStorage.setItem('firstName', firstName)
        localStorage.setItem('lastName', lastName)
        localStorage.setItem('role', role)
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', refreshToken)
        localStorage.setItem('tokenType', tokenType)

        console.log("Login successful, stored data:", {
          userId, username, userEmail, firstName, lastName, role,
          accessToken, refreshToken, tokenType,
        })

        // Notify app auth state changed, then navigate
        window.dispatchEvent(new Event('auth-changed'))
        navigate('/dashboard')
      } else {
        throw new Error(`Login failed with status: ${response.status}`)
      }
    } catch (err) {
      console.error("Login error:", err)
      console.error("Error response:", err.response)
      console.error("Error message:", err.message)

      let errorMessage = 'Login failed. Please try again.'

      if (err.response) {
        // Server responded with error status
        if (err.response.status === 401) {
          errorMessage = 'Invalid email or password'
        } else if (err.response.status === 403) {
          errorMessage = 'Access denied. Please contact administrator.'
        } else if (err.response.status === 404) {
          errorMessage = typeof err.response.data === 'string' ? err.response.data : 'Resource not found.'
        } else if (err.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.'
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message
        }
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
      setLoading(false)
    }
  }

  return (
      <div className="min-h-screen flex flex-row md:flex-row">
        {/* Left side - Logo */}
        <div className="w-full md:w-1/2 bg-gradient-to-br from-blue-900 to-indigo-900 flex items-center justify-center p-12">
          <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
          >
            <img
                src={bybLogo}
                alt="BYB Logo"
                className="mx-auto mb-8 max-w-full h-auto"
                style={{ maxHeight: '200px' }}
            />
            <h1 className="text-4xl font-bold text-white mb-4">Welcome to BYB Admin</h1>
            <p className="text-xl text-blue-200">Manage your platform with ease and efficiency</p>
          </motion.div>
        </div>

        {/* Right side - Login Form */}
        <div className="w-full md:w-1/2 flex items-center justify-center bg-gray-900 p-6">
          <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full max-w-md"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Sign In</h2>
              <p className="text-gray-400">Enter your credentials to access admin panel</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-white">Email</label>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-3 rounded-lg bg-gray-800 text-white placeholder-gray-400 border border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="admin@example.com"
                    required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-white">Password</label>
                <div className="relative">
                  <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full p-3 rounded-lg bg-gray-800 text-white placeholder-gray-400 border border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 pr-10"
                      placeholder="••••••••"
                      required
                  />
                  <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                  <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm text-center p-2 bg-red-900/30 rounded-lg"
                  >
                    {error}
                  </motion.div>
              )}

              <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full py-3 rounded-lg font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 ${
                      loading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  disabled={loading}
              >
                {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                ) : (
                    <div className="flex items-center justify-center">
                      <LogIn className="mr-2" />
                      Sign In
                    </div>
                )}
              </motion.button>
            </form>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p className='text-gray-500 text-sm'>Version: v1.0.0</p>
              <p className="text-gray-500 text-sm">Developed by TCY Consulting</p>
            </div>
          </motion.div>
        </div>
      </div>
  )
}